/**
 * Buscar por foto sin salir de la pagina (BUS-03, BUS-04, BUS-05).
 *
 * Es un panel colgado de la caja de busqueda, como el de sugerencias, y no una
 * pantalla aparte (rediseño del 14-09-2026, con el buscador por foto de
 * lowes.com como referencia): quien esta mirando una categoria y saca la foto
 * de la pieza no tiene por que irse a otra vista para eso. Pasa por tres
 * momentos, todos aca adentro: elegir la foto, recortarla y ver los parecidos.
 *
 * TRES DECISIONES QUE SE VEN EN EL PANEL:
 *
 * 1. El input NO lleva `capture`. Es tentador ponerlo —"que abra la camara"—
 *    pero `capture` hace lo contrario de lo que pide `BUS-04`: obliga a la
 *    camara y le saca la galeria a quien ya tenia la foto sacada. Sin el
 *    atributo, iOS y Android muestran su propio menu con las dos opciones, que
 *    es literalmente lo que la historia pide.
 * 2. Los resultados se llaman "parecidos", no "resultados". El primero acierta
 *    el producto exacto en tres de cada cuatro casos; presentarlos como LA
 *    respuesta seria mentirle al cuarto comprador.
 * 3. Por lo mismo, WhatsApp esta SIEMPRE, tambien cuando hay parecidos. En la
 *    busqueda por texto aparece solo si no hubo nada (BUS-06); aca el caso malo
 *    no es la lista vacia, es la lista llena en la que no esta lo que buscaba.
 *
 * La foto no se guarda en ningun lado: vive en memoria mientras el panel esta
 * abierto, se sube recortada y reducida, el servidor la compara y la descarta.
 */
import { useEffect, useState, type DragEvent } from 'react';

import type { ProductoResumen } from '@bandu/compartido';

import { ErrorDeApi } from '../../servicios/cliente-api';
import { buscarPorFoto } from '../../servicios/busqueda.servicio';
import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import {
  ErrorDeFoto,
  RECORTE_COMPLETO,
  leerFoto,
  liberarFoto,
  recortarFoto,
  type FotoLeida,
  type Recorte,
} from '../../utilidades/foto-busqueda';
import { whatsappBusquedaPorFoto } from '../../utilidades/whatsapp';
import { GrillaProductos } from '../catalogo/GrillaProductos';
import { Cargando } from '../comunes/EstadoPeticion';
import { Icono } from '../comunes/Icono';
import { RecortadorFoto } from './RecortadorFoto';

type Fase =
  | { nombre: 'elegir' }
  | { nombre: 'leyendo' }
  | { nombre: 'recortar' }
  | { nombre: 'buscando' }
  | { nombre: 'listo'; parecidos: ProductoResumen[]; miniatura: string }
  | { nombre: 'error'; mensaje: string };

export interface PanelBusquedaFotoProps {
  /** Id del panel, para el `aria-controls` del boton que lo abre. */
  id: string;
  onCerrar(): void;
}

function mensajeDe(error: unknown): string {
  if (error instanceof ErrorDeFoto || error instanceof ErrorDeApi) return error.message;
  return 'No pudimos buscar con esa foto. Intenta de nuevo.';
}

export function PanelBusquedaFoto({ id, onCerrar }: PanelBusquedaFotoProps) {
  const [fase, setFase] = useState<Fase>({ nombre: 'elegir' });
  const [foto, setFoto] = useState<FotoLeida | null>(null);
  const [recorte, setRecorte] = useState<Recorte>(RECORTE_COMPLETO);
  const [arrastrando, setArrastrando] = useState(false);

  // La foto y la miniatura son blobs del navegador: se liberan al cambiarlos y
  // al cerrar el panel, o quedan retenidos hasta que se cierra la pestaña.
  useEffect(() => {
    if (foto === null) return;
    return () => liberarFoto(foto);
  }, [foto]);
  const miniatura = fase.nombre === 'listo' ? fase.miniatura : null;
  useEffect(() => {
    if (miniatura === null) return;
    return () => URL.revokeObjectURL(miniatura);
  }, [miniatura]);

  async function elegir(archivo: File | undefined): Promise<void> {
    if (archivo === undefined) return;

    setFase({ nombre: 'leyendo' });
    try {
      setFoto(await leerFoto(archivo));
      setRecorte(RECORTE_COMPLETO);
      setFase({ nombre: 'recortar' });
    } catch (error) {
      setFase({ nombre: 'error', mensaje: mensajeDe(error) });
    }
  }

  async function buscar(): Promise<void> {
    if (foto === null) return;

    setFase({ nombre: 'buscando' });
    try {
      const pedazo = await recortarFoto(foto, recorte);
      const resultado = await buscarPorFoto(pedazo);
      setFase({
        nombre: 'listo',
        parecidos: resultado.productos,
        miniatura: URL.createObjectURL(pedazo),
      });
    } catch (error) {
      setFase({ nombre: 'error', mensaje: mensajeDe(error) });
    }
  }

  function soltarArchivo(evento: DragEvent<HTMLDivElement>): void {
    evento.preventDefault();
    setArrastrando(false);
    void elegir(evento.dataTransfer.files[0]);
  }

  const ocupado = fase.nombre === 'leyendo' || fase.nombre === 'buscando';
  const conFoto = foto !== null && (fase.nombre === 'recortar' || fase.nombre === 'buscando');

  return (
    <div className="panel-foto" id={id} role="region" aria-label="Buscar por foto">
      <header className="panel-foto-cab">
        <div>
          <p className="t">
            {fase.nombre === 'listo'
              ? 'Parecidos a tu foto'
              : conFoto
                ? 'Recorta lo que quieres buscar'
                : 'Buscar por foto'}
          </p>
          <p className="d">
            {fase.nombre === 'listo'
              ? 'Buscar por foto acierta la mayoria de las veces, pero no todas.'
              : conFoto
                ? 'Deja dentro del marco solo la pieza: el fondo confunde al buscador.'
                : 'La foto no se guarda: se usa solo para esta busqueda.'}
          </p>
        </div>
        <button type="button" className="modal-cerrar" onClick={onCerrar} aria-label="Cerrar">
          <Icono nombre="cerrar" />
        </button>
      </header>

      {/*
        Un `<input type="file">` de verdad, escondido detras del boton: es lo
        unico que abre el selector nativo del celular con camara y galeria.
        Sin `capture`, a proposito (ver el comentario de arriba).
      */}
      <input
        id={`${id}-archivo`}
        className="solo-lectores"
        type="file"
        accept="image/*"
        disabled={ocupado}
        onChange={(evento) => {
          void elegir(evento.target.files?.[0]);
          // Permite volver a elegir la MISMA foto: sin esto el `change` no
          // se dispara la segunda vez.
          evento.target.value = '';
        }}
      />

      {fase.nombre === 'elegir' || fase.nombre === 'leyendo' || fase.nombre === 'error' ? (
        <div
          className={arrastrando ? 'panel-foto-zona arrastrando' : 'panel-foto-zona'}
          onDragOver={(evento) => {
            evento.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={soltarArchivo}
        >
          <Icono nombre="galeria" />
          <p>
            Arrastra una foto hasta aca, o{' '}
            <label className="panel-foto-enlace" htmlFor={`${id}-archivo`}>
              sacala o elige una de tu galeria
            </label>
          </p>
          <p className="panel-foto-nota">Solo imagenes de hasta 8 MB.</p>
          {fase.nombre === 'leyendo' ? <Cargando que="la foto" /> : null}
          {fase.nombre === 'error' ? (
            <p className="foto-busqueda-error" role="alert">
              <Icono nombre="alerta" />
              {fase.mensaje}
            </p>
          ) : null}
        </div>
      ) : null}

      {conFoto && foto !== null ? (
        <>
          <RecortadorFoto
            src={foto.url}
            ancho={foto.ancho}
            alto={foto.alto}
            recorte={recorte}
            onChange={setRecorte}
          />
          <div className="panel-foto-acciones">
            <label className="btn btn-contorno" htmlFor={`${id}-archivo`}>
              Otra foto
            </label>
            <button
              type="button"
              className="btn btn-primario"
              disabled={ocupado}
              onClick={() => {
                void buscar();
              }}
            >
              <Icono nombre="buscar" />
              {fase.nombre === 'buscando' ? 'Buscando…' : 'Buscar parecidos'}
            </button>
          </div>
          {fase.nombre === 'buscando' ? <Cargando que="los parecidos" /> : null}
        </>
      ) : null}

      {fase.nombre === 'listo' ? (
        <>
          <div className="panel-foto-consulta">
            <img src={fase.miniatura} alt="El recorte con el que buscaste" />
            <div className="panel-foto-acciones">
              <button
                type="button"
                className="btn btn-sm btn-contorno"
                onClick={() => setFase({ nombre: 'recortar' })}
              >
                Ajustar el recorte
              </button>
              <label className="btn btn-sm btn-contorno" htmlFor={`${id}-archivo`}>
                Otra foto
              </label>
            </div>
          </div>

          {/* Sin `titula` y con rotulos en vez de encabezados: el panel vive
              en la cabecera, antes del h1 de la pagina, y un h2 ahi rompe la
              jerarquia de la vista (mismo criterio que el menu del catalogo). */}
          {fase.parecidos.length > 0 ? (
            <GrillaProductos productos={fase.parecidos} titulo="Productos parecidos a tu foto" />
          ) : null}

          <section className="rescate" aria-labelledby={`${id}-ninguno`}>
            <p className="t" id={`${id}-ninguno`}>
              <Icono nombre="lupaVacia" />
              {fase.parecidos.length > 0
                ? '¿Ninguno es el que buscas?'
                : 'No encontramos nada parecido'}
            </p>
            <p>Mandanos la foto por WhatsApp y te decimos exactamente cual es y si lo tenemos.</p>
            <p className="acc">
              <a
                className="btn btn-wa"
                href={whatsappBusquedaPorFoto()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => registrarClicWhatsapp({ origen: 'busqueda' })}
              >
                <Icono nombre="wa" />
                Mandar la foto por WhatsApp
              </a>
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}
